<template>
  <div class="container mt-5">
    <div class="card modern-card">
      <div class="card-header">
        <h4 class="mb-0">
          <i class="fab fa-paypal me-2"></i>
          Rechargement via PayPal
        </h4>
      </div>
      
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-3 text-muted">Préparation du paiement...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          <i class="fas fa-exclamation-circle me-2"></i>
          <span>{{ error }}</span>
        </div>

        <div v-else>
          <div class="payment-info mb-4">
            <h5 class="info-title">Détails du rechargement</h5>
            <div class="info-content">
              <div class="info-row">
                <span class="info-label">Compte prépayé</span>
                <span class="info-value">{{ accountNumber }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Solde actuel</span>
                <span class="info-value">{{ formatNumber(currentBalance) }} {{ currency }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Montant à recharger</span>
                <span class="info-value text-success fw-bold">{{ formatNumber(amount) }} {{ currency }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Nouveau solde estimé</span>
                <span class="info-value text-primary fw-bold">{{ formatNumber(currentBalance + amount) }} {{ currency }}</span>
              </div>
            </div>
          </div>

          <div class="alert alert-info-custom mb-4">
            <i class="fas fa-info-circle me-2"></i>
            Vous allez être redirigé vers PayPal pour finaliser votre rechargement en toute sécurité.
          </div>

          <div id="paypal-button-container" style="min-height: 150px;"></div>

          <div v-if="paypalLoading" class="text-center my-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <p class="text-muted mt-2 small">Chargement PayPal...</p>
          </div>

          <div class="mt-3 text-center">
            <router-link :to="`/prepaid-accounts`" class="btn btn-outline-secondary btn-cancel">
              <i class="fas fa-arrow-left me-2"></i>
              Annuler et retourner
            </router-link>
          </div>

          <div class="text-center mt-4">
            <small class="secure-badge">
              <i class="fas fa-lock me-1"></i>
              Paiement sécurisé par PayPal
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError, success as showSuccess } from '@/utils/utils';

export default defineComponent({
  name: 'RechargePayPal2',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const intentId = ref(route.query.intentId as string);
    const amount = ref(0);
    const currency = ref('EUROS');
    const accountNumber = ref('');
    const currentBalance = ref(0);
    const loading = ref(true);
    const error = ref('');
    const paypalLoading = ref(true);

    const formatNumber = (val: number) => new Intl.NumberFormat('fr-FR').format(val);

    const loadRechargeInfo = async () => {
      try {
        const { data } = await ApiService.get(`/prepaid/recharge/intent/${intentId.value}`);
        accountNumber.value = data.data.accountNumber;
        currentBalance.value = data.data.currentBalance;
        amount.value = data.data.amount;
        currency.value = data.data.currency;
        loading.value = false;
      } catch (err: any) {
        error.value = 'Impossible de charger les informations';
        loading.value = false;
      }
    };

    const initPayPal = () => {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.VUE_APP_PAYPAL_CLIENT_ID}&currency=USD`;
      script.onload = () => renderPayPalButtons();
      script.onerror = () => { 
        error.value = 'Impossible de charger PayPal'; 
        paypalLoading.value = false; 
      };
      document.head.appendChild(script);
    };

    const renderPayPalButtons = () => {
      if (!(window as any).paypal) return;

      (window as any).paypal.Buttons({
        createOrder: async () => {
          const { data } = await ApiService.post('/prepaid/recharge/paypal/create-order', { 
            intentId: intentId.value 
          });
          return data.data.orderID;
        },
        onApprove: async (data: any) => {
          try {
            await ApiService.post('/prepaid/recharge/paypal/capture-order', {
              orderID: data.orderID,
              intentId: intentId.value
            });
            showSuccess('Rechargement réussi !');
            setTimeout(() => router.push('/listePrepaidAccount'), 2000);
          } catch (err) {
            showError('Erreur lors de la confirmation');
          }
        },
        onCancel: () => router.push('/prepaid-accounts'),
        onError: () => showError('Erreur PayPal')
      }).render('#paypal-button-container').finally(() => {
        paypalLoading.value = false;
      });
    };

    onMounted(async () => {
      if (!intentId.value) {
        error.value = 'Lien invalide';
        loading.value = false;
        return;
      }
      await loadRechargeInfo();
      if (!error.value) initPayPal();
    });

    return { 
      intentId, 
      amount, 
      currency, 
      accountNumber, 
      currentBalance, 
      loading, 
      error, 
      paypalLoading, 
      formatNumber 
    };
  }
});
</script>

<style scoped>
.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #0070ba 0%, #1546a0 100%);
  color: white !important;
  padding: 1.5rem;
  border: none;
}

.card-header h4 {
  font-weight: 600;
  font-size: 1.25rem;
  color: white !important;
}

.card-body {
  padding: 2rem;
}

.payment-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #0070ba;
}

.info-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 2px solid #dee2e6;
}

.info-label {
  color: #6c757d;
  font-size: 0.95rem;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

.alert-info-custom {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 1rem;
  color: #0070ba;
}

#paypal-button-container {
  min-height: 150px;
  max-width: 500px;
  margin: 0 auto;
}

.btn-cancel {
  border-radius: 8px;
  padding: 0.625rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.secure-badge {
  display: inline-flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.secure-badge i {
  color: #28a745;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-width: 0.25em;
}

@media (max-width: 576px) {
  .card-body {
    padding: 1.5rem;
  }
  
  .payment-info {
    padding: 1rem;
  }
}
</style>